import { Suspense, useEffect, useState } from 'react';
import { ngRoute } from '../../routing/ngRoute';
import { LazyModule } from '../../lazyModule';

import { API_URL } from '../../../legacy/common/consts.js';
import { AngularInjector } from '../../../common/AngularUtils';
import {
    createLogsDataLoadedHandler,
    extractEntityModeIdFromPathname,
    extractOotbSourceFromKubernetesEntitiesPath,
    resolveOotbSearchJobSource,
} from './logsChartWrappersUtils';

function LogsChartContainerWrapper({
    auth,
    chartModel,
    themeKey,
    chartHeight,
    urlOverridesService,
    setLinkUrl,
    filters,
    replaceOnlyFilters,
    setLogsFilters,
    setLogsIndexes,
    setLogsTimeRange,
    routeParameterService,
    relatedContentService,
    $scope,
    integrations,
    featureFlags,
    requestOverlayContainer,
    removeOverlayContainer,
    isBuiltInChart,
    setLogsChartJobErrors,
}) {
    const [time, setTime] = useState(urlOverridesService.getGlobalTimePicker());

    const currentUser = AngularInjector.useInjectedClass('currentUser');

    useEffect(() => {
        const unregisterRouteWatchGroup = routeParameterService.registerRouteWatchGroup(
            ['startTime', 'endTime', 'startTimeUTC', 'endTimeUTC'],
            () => {
                setTime(urlOverridesService.getGlobalTimePicker());
            }
        );
        return () => {
            // Clean up the subscription
            unregisterRouteWatchGroup();
        };
    });

    /**
     * Calling setSourceFilterOverrideList from within the React component didn't seem to be triggering the $digest cycle
     * see https://docs.angularjs.org/guide/$location#using-location-outside-of-the-scope-life-cycle
     * Forcing the digest to trigger by doing a manual $apply call after the filter is updated
     */
    const urlOverridesServiceWithApplyOnGetSourceFilterOverrideList = {
        ...urlOverridesService,
        setSourceFilterOverrideList: (newFilters) => {
            urlOverridesService.setSourceFilterOverrideList(newFilters);
            $scope.$apply();
        },
    };

    // $location gives the clean Angular path and search params, used to identify
    // the olly entity detail context and new Kubernetes entities experience.
    const $location = AngularInjector.useInjectedClass('$location');
    const locationPath = $location?.path?.();
    const entityModeId = extractEntityModeIdFromPathname(locationPath);
    const kubernetesEntitiesSource = extractOotbSourceFromKubernetesEntitiesPath(
        locationPath,
        $location?.search?.()?.entityView
    );

    const handleLogsDataLoaded = createLogsDataLoadedHandler($scope, chartModel);
    const navigatorModeId = ngRoute.params?.modeID ?? entityModeId;
    const k8sSelectedTab = ngRoute.params?.selectedTab;

    return (
        <Suspense fallback={<div>Loading...</div>}>
            <LazyModule
                artifact={{
                    bundleJS: window.signalviewConfig?.logsChartJS,
                    globalPropertyName: 'logsChart',
                }}
                currentUser={currentUser}
                authToken={auth.authToken()}
                chartModel={chartModel}
                themeKey={themeKey}
                chartHeight={chartHeight}
                API_URL={API_URL}
                urlOverridesService={urlOverridesServiceWithApplyOnGetSourceFilterOverrideList}
                setLogsUrl={setLinkUrl}
                onLogsDataLoaded={handleLogsDataLoaded}
                globalTime={time}
                filters={filters || urlOverridesService.getSourceFilterOverrideList()}
                replaceOnlyFilters={replaceOnlyFilters}
                relatedContentService={relatedContentService}
                history={ngRoute.history}
                setLogsFilters={setLogsFilters}
                setLogsIndexes={setLogsIndexes}
                setLogsTimeRange={setLogsTimeRange}
                integrations={integrations}
                featureEnabled={(key) => featureFlags[key] !== undefined && featureFlags[key]}
                searchJobSource={resolveOotbSearchJobSource(
                    isBuiltInChart,
                    navigatorModeId,
                    k8sSelectedTab,
                    kubernetesEntitiesSource
                )}
                createOverlayContainer={(attributes) =>
                    requestOverlayContainer(chartModel.sf_id, attributes)
                }
                removeOverlayContainer={(mode) => removeOverlayContainer(chartModel.sf_id, mode)}
                onLogsChartJobError={
                    typeof setLogsChartJobErrors === 'function' ? setLogsChartJobErrors : undefined
                }
            />
        </Suspense>
    );
}

LogsChartContainerWrapper.displayName = 'LogsChartContainer';
export default LogsChartContainerWrapper;
